import API, { API_URL } from '../Api/api';

const PRESENCE_TOKEN_KEY = 'active_status_token';
const HEARTBEAT_MS = 30000;

let heartbeatTimer = null;
let listenersBound = false;
let tabIsActive = true;

export const storePresenceToken = (token) => {
  if (token) localStorage.setItem(PRESENCE_TOKEN_KEY, token);
  else localStorage.removeItem(PRESENCE_TOKEN_KEY);
};

export const getPresenceToken = () => localStorage.getItem(PRESENCE_TOKEN_KEY);

const isLoggedIn = () => Boolean(localStorage.getItem('user_access_token'));

const isTabVisible = () =>
  typeof document === 'undefined' || document.visibilityState === 'visible';

const canSendHeartbeat = () => isLoggedIn() && tabIsActive && isTabVisible();

const resumePresence = async () => {
  if (!isLoggedIn()) return null;
  try {
    const res = await API.post('/presence/resume', { platform: 'web' });
    const token = res.data?.active_status_token;
    if (token) storePresenceToken(token);
    return token || null;
  } catch {
    return null;
  }
};

const sendOffline = () => {
  const token = getPresenceToken();
  if (!token) return;

  const payload = JSON.stringify({ active_status_token: token });
  const url = `${API_URL}/presence/offline`;

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([payload], { type: 'application/json' });
    navigator.sendBeacon(url, blob);
  }

  const access = localStorage.getItem('user_access_token');
  const headers = { 'Content-Type': 'application/json' };
  if (access) headers.Authorization = `Bearer ${access}`;

  fetch(url, {
    method: 'POST',
    headers,
    body: payload,
    keepalive: true,
  }).catch(() => {});
};

const sendHeartbeat = async () => {
  if (!canSendHeartbeat()) return;

  let token = getPresenceToken();
  if (!token) token = await resumePresence();
  if (!token || !canSendHeartbeat()) return;

  try {
    await API.post('/presence/heartbeat', { active_status_token: token });
    if (!canSendHeartbeat()) sendOffline();
  } catch (err) {
    if (!canSendHeartbeat()) return;
    if (err?.response?.status === 404) {
      const fresh = await resumePresence();
      if (fresh && canSendHeartbeat()) {
        await API.post('/presence/heartbeat', { active_status_token: fresh }).catch(() => {});
        if (!canSendHeartbeat()) sendOffline();
      }
    }
  }
};

const handleTabHidden = () => {
  tabIsActive = false;
  clearHeartbeat();
  sendOffline();
};

const handleTabVisible = () => {
  tabIsActive = true;
  sendHeartbeat();
  if (!heartbeatTimer && isTabVisible()) {
    heartbeatTimer = setInterval(() => { sendHeartbeat(); }, HEARTBEAT_MS);
  }
};

const bindLifecycle = () => {
  if (listenersBound) return;
  listenersBound = true;
  tabIsActive = isTabVisible();

  window.addEventListener('beforeunload', () => {
    tabIsActive = false;
    clearHeartbeat();
    sendOffline();
  });
  window.addEventListener('pagehide', (e) => {
    if (!e.persisted) {
      tabIsActive = false;
      clearHeartbeat();
      sendOffline();
    }
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') handleTabHidden();
    else if (document.visibilityState === 'visible') handleTabVisible();
  });
};

const clearHeartbeat = () => {
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  heartbeatTimer = null;
};

export const startPresence = async () => {
  clearHeartbeat();
  if (!isLoggedIn()) return;

  tabIsActive = isTabVisible();
  bindLifecycle();

  if (tabIsActive) {
    await sendHeartbeat();
    heartbeatTimer = setInterval(() => { sendHeartbeat(); }, HEARTBEAT_MS);
  }
};

export const stopPresence = ({ sendOfflineSignal = true, clearToken = true } = {}) => {
  tabIsActive = false;
  clearHeartbeat();
  if (sendOfflineSignal) sendOffline();
  if (clearToken) storePresenceToken(null);
};
